import {uuid} from '@/src/shared/utils/functions'

/**
 * Base Domain Event interface
 *
 * Domain Events represent something that happened in the domain that domain experts care about.
 * They are immutable and named in past tense (e.g., ProductAddedToBasket, OrderCreated).
 *
 * Key principles:
 * - Events are facts that happened - they are immutable
 * - Events are named in past tense
 * - Events contain all data needed by handlers
 * - Events should be small and focused
 */
export interface DomainEvent<TPayload = unknown> {
  /** Unique event type identifier (e.g., 'basket.product_added') */
  readonly eventType: string

  /** When the event occurred */
  readonly occurredOn: Date

  /** Unique identifier for this event instance */
  readonly eventId: string

  /** Event payload data */
  readonly payload?: TPayload

  /** Optional aggregate ID this event belongs to */
  readonly aggregateId?: string
}

/**
 * Event handler function type
 */
export type EventHandler<T extends DomainEvent = DomainEvent> = (event: T) => void | Promise<void>

/**
 * Event subscription that can be unsubscribed
 */
export interface EventSubscription {
  unsubscribe(): void
}

/**
 * Helper to create domain events with auto-generated metadata
 */
export function createDomainEvent<TPayload>(
  eventType: string,
  payload?: TPayload,
  aggregateId?: string,
): DomainEvent<TPayload> {
  return {
    eventType,
    occurredOn: new Date(),
    eventId: uuid(),
    payload,
    aggregateId,
  }
}
